import { getReturnOnboardingData } from '@/service/returnChatService';
import type { ReturnOnboardingDetails } from '@/types';
import type { ReturnCase, ReturnOptions, SubmittedReturn } from '@/types/returns';
import { buildReturnCase } from '@/utils/returnCases';
import { useDashboardQuery } from './useDashboardQuery';

/**
 * Options for the return form (R1–R6). `null` means returns from the dashboard aren't live
 * yet: the backend has no endpoints for pickup slots or refund details, so only the local
 * demo session has options. `sample` picks a designed demo state (see demoReturnOptions).
 */
export function useReturnOptions(sample?: string | null) {
  const { data, ...rest } = useDashboardQuery<ReturnOptions | null>(
    async (demo) => {
      if (import.meta.env.DEV && demo) return (await import('@/pages/dev/demoData')).demoReturnOptions(sample);
      return null;
    },
    null,
    'We couldn’t load your return options. Please try again.',
  );
  return { options: data, ...rest };
}

/**
 * The latest return on an order, for the status page (R7–R8); `null` when there isn't one.
 * In the demo session, `sample` and `submitted` pick what to show (see demoReturnCase).
 */
export function useReturnCase(
  orderId: string | undefined,
  sample?: string | null,
  submitted?: SubmittedReturn,
) {
  const { data, ...rest } = useDashboardQuery<ReturnCase | null>(
    async (demo) => {
      if (import.meta.env.DEV && demo) return (await import('@/pages/dev/demoData')).demoReturnCase(sample, submitted);
      if (!orderId) return null;
      const data: ReturnOnboardingDetails | undefined = (await getReturnOnboardingData(orderId))?.data;
      const latest = [...(data?.returns ?? [])].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
      if (!latest) return null;
      return buildReturnCase(latest, data?.product_info?.find((p) => String(p.id) === String(latest.product_id))?.title);
    },
    null,
    'We couldn’t load this return. Please try again.',
  );
  return { returnCase: data, ...rest };
}
